import { Rng, seedFromString } from '@gardener/core';
import type { PaletteKey } from '../palette';
import { PixelCanvas } from './canvas';

export interface FrameContext {
  /** Seeded from sprite name + anim + frame index: deterministic across builds. */
  rng: Rng;
  anim: string;
  frame: number;
}

/** A frame is a text grid or a procedural draw function over a blank canvas of the sprite's size. */
export type FrameSource = string | ((c: PixelCanvas, ctx: FrameContext) => void);

export interface AnimDef {
  /** Playback rate. Omit for static frames / tile variants. */
  fps?: number;
  loop?: boolean;
  frames: readonly FrameSource[];
}

export type SpriteGroup = 'actor' | 'prop' | 'tile' | 'fx' | 'ui';

export interface SpriteDef {
  /** snake_case, unique. Becomes the key in the typed manifest. */
  name: string;
  group: SpriteGroup;
  size: readonly [w: number, h: number];
  /**
   * Art pixels per world pixel (ADR-0064, ADR-0073): 4 draws the sprite with four times the pixels at a quarter scale,
   * so a 64×72 gardener stands as tall in the garden as a 16×18 one. `size` (a multiple of it) and `origin` stay in
   * art pixels. Default 1.
   */
  density?: 1 | 2 | 4;
  /** Pivot in pixels (feet for actors, grip for weapons). Default: top-left. */
  origin?: readonly [x: number, y: number];
  /**
   * A held gun's grip: where its holder's fist goes (art px, the fist's top-left). The client draws the gardener's own
   * fist there, in their skin (ADR-0073); a gun without it is held without a hand showing (the Robo Fist's gauntlet).
   */
  hand?: readonly [x: number, y: number];
  anims: Record<string, AnimDef>;
  /** Auto-outline colour applied to every frame after drawing (sprite must keep a 1px margin). */
  outline?: PaletteKey;
  /**
   * Generate white silhouettes (`<frame>@flash`) for hit flashes: every frame's, or only those of the anims named (a
   * gun's `idle`, outlined when it lies on the ground, ADR-0086).
   */
  flash?: boolean | readonly string[];
  /** Recoloured copies emitted as sprites named `<name>_<variant>`. */
  recolor?: Record<string, Partial<Record<PaletteKey, PaletteKey>>>;
  /** Intent, constraints, and the latest self-critique (see .claude/skills/pixel-art). */
  notes?: string;
  /**
   * Seeds procedural frames' `rng` instead of `name` (seed = `<seed>/<anim>/<frame>`). Set it when renaming a
   * procedural sprite so its random scatter stays exactly as reviewed.
   */
  seed?: string;
}

export const sprite = <const T extends SpriteDef>(def: T): T => def;

/** Keep a 1× sprite's world size while moving it onto the world's 4× grid. */
export function denseSprite<const T extends SpriteDef>(
  def: T,
): Omit<T, 'size' | 'origin' | 'hand' | 'density' | 'anims'> & SpriteDef {
  if ((def.density ?? 1) !== 1) return def;
  return {
    ...def,
    size: [def.size[0] * 4, def.size[1] * 4],
    origin: def.origin && [def.origin[0] * 4, def.origin[1] * 4],
    hand: def.hand && [def.hand[0] * 4, def.hand[1] * 4],
    density: 4,
    // ponytail: Scale2x keeps the old silhouette; redraw native 4× details when these sprites need more polish.
    anims: Object.fromEntries(
      Object.entries(def.anims).map(([key, anim]) => [
        key,
        {
          ...anim,
          frames: anim.frames.map((frame) => (c: PixelCanvas, ctx: FrameContext) => {
            const source = typeof frame === 'string' ? PixelCanvas.fromGrid(frame) : new PixelCanvas(...def.size);
            if (typeof frame !== 'string') frame(source, ctx);
            const dense = source.scale2x().scale2x();
            dense.pixels.forEach((pixel, i) => {
              c.pixels[i] = pixel;
            });
          }),
        },
      ]),
    ),
  };
}

/**
 * Frame `index` of `def`'s `anim` as drawn (its grid, or its draw function on a blank canvas seeded `<seed>/<anim>/<i>`),
 * outlined: what the atlas compiles (tools/assetc), and what the admin app draws skins from (ADR-0157).
 */
export function renderFrame(def: SpriteDef, anim: string, index: number): PixelCanvas {
  const [w, h] = def.size;
  const src = def.anims[anim]?.frames[index];
  const where = `${def.name}/${anim}/${index}`;
  let canvas: PixelCanvas;
  if (typeof src === 'string') {
    canvas = PixelCanvas.fromGrid(src, where);
    if (canvas.w !== w || canvas.h !== h) {
      throw new Error(`${where}: grid is ${canvas.w}×${canvas.h}, sprite size is ${w}×${h}`);
    }
  } else if (typeof src === 'function') {
    canvas = new PixelCanvas(w, h);
    const seed = `${def.seed ?? def.name}/${anim}/${index}`;
    src(canvas, { rng: new Rng(seedFromString(seed)), anim, frame: index });
  } else {
    throw new Error(`${where}: missing frame`);
  }
  if (def.outline) canvas.outline(def.outline);
  if (canvas.pixels.every((p) => p === null)) throw new Error(`${where}: frame is empty`);
  return canvas;
}

/** Team colours: team things are authored blue (b, B, c) and recoloured red (r, R, i). */
export const TEAM_RECOLOR = {
  red: { b: 'r', B: 'R', c: 'i' },
} as const satisfies Record<string, Partial<Record<PaletteKey, PaletteKey>>>;

/** Convenience: n frames from one draw function. */
export const frames = (n: number, draw: (c: PixelCanvas, ctx: FrameContext) => void): FrameSource[] =>
  Array.from({ length: n }, () => draw);

export interface FontDef {
  name: string;
  /** Glyph height in pixels; widths come from each glyph grid (proportional font). */
  height: number;
  /** Pixels between glyphs. */
  spacing: number;
  /** Advance for ' '. */
  spaceWidth: number;
  /** Grids drawn with `w` (ink) and `.` — tinted at runtime. */
  glyphs: Record<string, string>;
  /** Also emit an outlined variant (`<name>_outline`) with this outline colour. */
  outline?: PaletteKey;
}

export const font = <const T extends FontDef>(def: T): T => def;
